import { isDefined } from "@clipboard-health/util-ts";
import { Alert, Box, Stack } from "@mui/material";
import { BaaText } from "@src/appV2/redesign/InHome/BaaDialog/BaaText";
import { Card, Divider, Text, Title } from "@src/appV2/redesign/system/ui-components";
import { type Worker } from "@src/appV2/Worker/api/types";
import { useLayoutEffect, useRef, useState } from "react";
import SignaturePad from "react-signature-pad-wrapper";

import { TextButton } from "../../components/TextButton";

interface BaaDialogContentProps {
  worker: Worker;
  signature: string;
  setSignature: (signature: string) => void;
  baaRef: React.RefObject<HTMLDivElement>;
  isLoadingSign: boolean;
  isErrorSign: boolean;
}

export function BaaDialogContent(props: BaaDialogContentProps) {
  const { worker, signature, setSignature, isLoadingSign, isErrorSign, baaRef } = props;

  const signaturePadRef = useRef<SignaturePad>(null);

  if (signature) {
    signaturePadRef?.current?.fromDataURL(signature);
  }

  // Explicitly using null here since the ref is of type SignaturePad | null
  const [current, setCurrent] = useState<SignaturePad | null>(signaturePadRef.current);

  useLayoutEffect(() => {
    setCurrent(signaturePadRef.current);
    if (current === null) {
      return;
    }

    /*
      Calling this to resize the signature pad on initial render correctly.
      When this is not present, signature pad has invalid size when in certain
      flex containers.
    */
    current.handleResize();
    const onEndStroke = () => {
      const signature = current?.toDataURL();

      if (isDefined(signature)) {
        setSignature(signature);
      }
    };

    const onBeginStroke = () => {
      current.handleResize();
    };

    current.instance.addEventListener("endStroke", onEndStroke);
    current.instance.addEventListener("beginStroke", onBeginStroke);

    return () => {
      current.instance.removeEventListener("endStroke", onEndStroke);
      current.instance.removeEventListener("beginStroke", onBeginStroke);
    };
  }, [current, setSignature]);

  return (
    <>
      <Stack
        spacing={2}
        sx={{ backgroundColor: (theme) => theme.palette.background.tertiary, px: 8 }}
      >
        <Title component="h3">Business Associate Agreement</Title>
        <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
          You are going to be handling sensitive patient information. Make sure you read through the
          entire agreement before signing.
        </Text>

        <BaaText ref={baaRef} contractorName={worker.name} contractorSignature={signature} />
      </Stack>
      <Stack
        spacing={5}
        sx={() => ({
          backgroundColor: (theme) => theme.palette.background.primary,
          padding: 7,
        })}
      >
        <Text variant="h5">Please add your signature</Text>
        <Text variant="caption">Signature*</Text>
        <Card outlined sx={{ p: 5 }}>
          <SignaturePad ref={signaturePadRef} redrawOnResize />
          <Divider sx={{ borderColor: (theme) => theme.palette.border?.primary, mb: 7 }} />
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <TextButton
              underline={false}
              disabled={isLoadingSign || signature === ""}
              onClick={() => {
                signaturePadRef?.current?.clear();
                setSignature("");
              }}
            >
              Clear
            </TextButton>
          </Box>
        </Card>
        {isErrorSign && <Alert severity="error">Error submitting signature</Alert>}
      </Stack>
    </>
  );
}
